import { useEffect, useRef } from "react";

export default function useVisualizer(audioRef, playbackState, audioContextRef, analyserRef, rafIdRef) {
    const sourceRef = useRef(null);
    const dataArrayRef = useRef(new Uint8Array(128));

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        let audioCtx = audioContextRef.current;
        if (!audioCtx) {
            audioCtx = new (window.AudioContext || window.webkitAudioContext)();
            audioContextRef.current = audioCtx;
        }

        if (playbackState !== "playing") {
            if (rafIdRef.current) {
                cancelAnimationFrame(rafIdRef.current);
                rafIdRef.current = null;
            }
            return;
        }

        if (audioCtx.state === "suspended") {
            audioCtx.resume().catch(() => { });
        }

        if (!sourceRef.current) {
            const source = audioCtx.createMediaElementSource(audioRef.current);
            sourceRef.current = source;

            const analyserNode = audioCtx.createAnalyser();
            analyserNode.fftSize = 64;
            source.connect(analyserNode);
            analyserNode.connect(audioCtx.destination);
            analyserRef.current = analyserNode;

            const bufferLength = analyserNode.frequencyBinCount;
            const array = new Uint8Array(bufferLength);

            const tick = () => {
                requestAnimationFrame(tick);
                analyserNode.getByteFrequencyData(array);
                dataArrayRef.current.set(array);
            };

            requestAnimationFrame(tick);
        }
    }, [playbackState]);

    useEffect(() => {
        if (!audioContextRef.current) return;

        const audioCtx = audioContextRef.current;

        const silentOsc = audioCtx.createOscillator();
        const gainNode = audioCtx.createGain();

        gainNode.gain.value = 0;
        silentOsc.connect(gainNode).connect(audioCtx.destination);
        silentOsc.start();

        return () => {
            silentOsc.stop();
            silentOsc.disconnect();
            gainNode.disconnect();
        };
    }, [audioContextRef]);

    return { dataArrayRef };
}
